/* global location */
import { connectHits } from 'instantsearch.js/es/connectors'
import { appendStructuredDataCarousel } from '../../utils/structuredData'
import renderAndEnableListingTiles from '../listing_tile_rendering/listingTileRenderer'
import { setPLPUsedIndex, getOptionalFilters, hardSortActive } from '../../utils/search/helpers'
import { typesenseEnabled, rankingField } from '../../libs/search/searchClient'
import { getListingImpressionsSchema } from '../../analytics/AnalyticsHelpers'
import { registerNewImpressions } from '../../analytics/utils/impressions'
import { pushToDataLayer } from '../../analytics/PushToDataLayer'
import { timeoutHaltPromise } from '../../utils/jsHelpers'
import { nonSponsoredListingsPath, waitSponsoredListings, mergeSponsoredListings, shouldShowSponsoredListingsInMainResults } from '../sponsored_listings/helpers'
import { trackSponsoredListingImpression } from '../sponsored_listings/apiClient'

export const customHitsWidget = connectHits(renderOpts => {
  let hits
  const hitsElement = document.getElementById('hits')
  if (hitsElement === null) return
  const sponsoredHitsElement = document.getElementById('vjs-sponsored-listings-container')

  if (renderOpts.results === undefined) {
    hits = null
  } else {
    hits = renderOpts.results.hits

    setPLPUsedIndex(renderOpts)

    if (hits[0] === undefined) {
      hits = []
    }

    const structuredDataShouldBeDisplayed = !window.location.href.includes('?') &&
      !window.location.href.includes('search') &&
      !window.location.href.includes('showcases') &&
      !window.location.href.includes('sale')
    if (structuredDataShouldBeDisplayed) appendStructuredDataCarousel(hits)

    // firstRender should only be true if we've server side rendered the hits
    // this is an attempt to stop us fetching results on client-side when we have them server-side.
    const firstRender = JSON.parse(hitsElement.getAttribute('data-first-render'))
    if (firstRender === true) {
      // this setTimeout is here as sometimes there is immediately a second client-side render after the first
      setTimeout(() => {
        document.getElementById('hits').dataset.firstRender = 'false'
      }, 1000)
    }

    const urlParams = new URLSearchParams(window.location.search)
    const { plpPage, showSignUpTile, showOnboardingTile } = hitsElement.dataset
    const page = renderOpts?.results?.page
    const query = renderOpts?.results?.query ? renderOpts.results.query : ''
    const queryID = renderOpts?.results?.queryID ? renderOpts.results.queryID : ''
    const tileOptions = {
      plpPage: plpPage === 'true',
      showValueUSPTile: 'true', // This is the fallback so no need to check or set it on the backend
      showSignUpTile: showSignUpTile === 'true' && page === 0,
      showOnboardingTile: showOnboardingTile === 'true' && page === 0,
      rankingDebug: urlParams.get('ranking_debug')
    }

    const optionalFilters = getOptionalFilters()

    // In-page merch re-rank for personalised results. Server-side twin:
    // AlgoliaPlpSearchService#results — the two must agree or the grid
    // reshuffles after the hydrated first paint. Hard sorts win untouched.
    if (optionalFilters.length > 0) {
      if (typesenseEnabled()) {
        if (!hardSortActive(renderOpts)) sortHitsByCompactedBoostScore(hits)
      } else {
        // Skip custom ranking if we're already using a sorted index (e.g., price_asc, sale_percentage_desc)
        const indexUsed = renderOpts?.results?._rawResults?.[0]?.indexUsed
        const shouldSkipCustomRanking = indexUsed && shouldSkipSortingForIndex(indexUsed)

        if (!shouldSkipCustomRanking) {
          sortHitsWithCustomRanking(hits)
        }
      }
    }

    hits.forEach((hit, index) => {
      hit.__position = index + 1
      hit.__queryID = queryID
    })

    if (!firstRender) {
      // when shouldShowSponsoredListingsInMainResults() is true, the sponsoredHitsElement is not present - we use the main hits element to house the sponsored listings instead
      if (nonSponsoredListingsPath(location, page) || (sponsoredHitsElement === null && !shouldShowSponsoredListingsInMainResults())) {
        renderResults(hitsElement, hits, sponsoredHitsElement, tileOptions, query, page)
      } else {
        // Wait in case results of the Sponsored Listings query are not available already
        // most likely they will as that query is faster than the query on our production index
        const timeoutError = Symbol('Timeout Error')
        timeoutHaltPromise(waitSponsoredListings(), 1000, timeoutError).then((sponsoredListingsHits) => {
          if (sponsoredListingsHits.length > 0) {
            renderResultsWithSponsoredListings(hitsElement, hits, sponsoredHitsElement, sponsoredListingsHits, tileOptions, query, page)
          } else {
            renderResults(hitsElement, hits, sponsoredHitsElement, tileOptions, query, page)
          }
        }).catch(e => {
          console.log('Sponsored Listings promise failed or took too long', e)
          if (page !== 0 && !page) return

          // sometimes the pageToRender is different than the current page due to network issues (like a slow connection)
          // ensure the pageToRender is the same as the one in the request params before calling renderResults
          const params = new URLSearchParams(location.search)
          const currentPage = parseInt(params.get('p')) || 1 // add default value since first page has no pagination param
          const pageToRender = page + 1 // results page field starts at zero
          if (pageToRender === currentPage) {
            renderResults(hitsElement, hits, sponsoredHitsElement, tileOptions, query, page)
          }
        })
      }
    } else {
      // First page render - from server side
      const element = document.querySelector('[data-id="content-sponsored-listings"]')
      const sponsoredHits = JSON.parse(element.innerHTML)
      if (sponsoredHits.length !== 0) {
        const mergedHits = mergeSponsoredListings(hits, sponsoredHits)
        registerNewImpressions(getListingImpressionsSchema(mergedHits, query, page))
        const sponsoredListingIds = sponsoredHits.map(h => h.objectID)
        trackSponsoredListingImpression(sponsoredListingIds)
      } else {
        registerNewImpressions(getListingImpressionsSchema(hits, query, page))
      }
    }

    const connectHitsRenderProperties = {
      first_render: firstRender
    }
    pushToDataLayer('sp - connect_hits_render', connectHitsRenderProperties)
  }
})

function renderResults (hitsElement, hits, sponsoredHitsElement, tileOptions, query, page) {
  if (sponsoredHitsElement) sponsoredHitsElement.classList.add('hidden')
  registerNewImpressions(getListingImpressionsSchema(hits, query, page))
  renderAndEnableListingTiles(hitsElement, hits, tileOptions)
}

function renderResultsWithSponsoredListings (hitsElement, hits, sponsoredHitsElement, sponsoredListingsHits, tileOptions, query, page) {
  // Add Algolia queryID to any sponsoredHit that also exists in the current non sponsored results
  sponsoredListingsHits.forEach((sponsoredHit, index) => {
    sponsoredHit.__position = index + 1
    const existingHit = hits.find(h => h.objectID === sponsoredHit.objectID)
    if (existingHit) {
      sponsoredHit.__queryID = existingHit.__queryID
    }
  })

  const mergedHitsForTracking = mergeSponsoredListings(hits, sponsoredListingsHits)
  const allHits = [...sponsoredListingsHits, ...hits]
  const hitsToRender = shouldShowSponsoredListingsInMainResults() ? allHits : hits
  renderAndEnableListingTiles(hitsElement, hitsToRender, tileOptions)
  registerNewImpressions(getListingImpressionsSchema(mergedHitsForTracking, query, page))

  if (!shouldShowSponsoredListingsInMainResults()) {
    // render and enable sponsored listing tiles in their own separate container
    renderAndEnableListingTiles(sponsoredHitsElement, sponsoredListingsHits)
    sponsoredHitsElement.classList.remove('hidden')
  }

  const sponsoredListingIds = sponsoredListingsHits.map(h => h.objectID)
  trackSponsoredListingImpression(sponsoredListingIds)
}

// Typesense flavour of the in-page re-rank. compacted_boost_score (or the
// forced ranking variant's float) packs the freshness/boost/views/recency
// chain below into one integer, and Array#sort is stable, so equal scores
// keep the engine's personalised order. Same field as the server re-rank.
function sortHitsByCompactedBoostScore (hits) {
  const field = rankingField()
  hits.sort((a, b) => (b[field] || 0) - (a[field] || 0))
}

function sortHitsWithCustomRanking (hits) {
  hits.sort((a, b) => {
    // Sort by freshness_threshold (ascending)
    if (a.freshness_threshold !== b.freshness_threshold) {
      return a.freshness_threshold - b.freshness_threshold
    }

    // Sort by boost_score (descending)
    if (a.boost_score !== b.boost_score) {
      return b.boost_score - a.boost_score
    }

    // If boost_score is the same, sort by page_views (descending)
    if (a.page_views !== b.page_views) {
      return b.page_views - a.page_views
    }

    // If page_views is also the same, sort by published_at_int (descending)
    return b.published_at_int - a.published_at_int
  })
}

function shouldSkipSortingForIndex (indexUsed) {
  // Extract the environment part (e.g., 'production', 'staging', 'development')
  const envMatch = indexUsed.match(/^Listing_([^_]+)/)
  if (!envMatch) return false

  const env = envMatch[1]
  const baseIndexPattern = `Listing_${env}`

  // If the index name is longer than the base pattern, check what comes after
  if (indexUsed.length > baseIndexPattern.length) {
    const suffix = indexUsed.substring(baseIndexPattern.length)
    // Skip sorting if there's a suffix that's not a variant index (_var_1, _var_2, etc.)
    return suffix !== '' && !suffix.match(/^_var_\d+$/)
  }

  return false
}
